import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sanitizeInput, validateName, validateOrganization, validateEmail, validatePhone, validateMessage } from '../utils/validation';
import './SignupPage.css';

const logoUrl = process.env.PUBLIC_URL + '/connect-lane-logo.png';

const SignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    organizationName: '',
    country: '',
    mobileNumber: '',
    email: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  const countries = [
    'United States', 'India', 'United Kingdom', 'Canada', 'Australia',
    'Germany', 'France', 'Mexico', 'Brazil', 'Japan', 'China', 'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Sanitize input to prevent XSS attacks
    const sanitized = sanitizeInput(value);
    setFormData({
      ...formData,
      [name]: sanitized
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!validateName(formData.name)) {
      newErrors.name = 'Name is required and can only contain letters, spaces, hyphens, and apostrophes';
    }

    if (!validateOrganization(formData.organizationName)) {
      newErrors.organizationName = 'Organization name is required and can only contain letters, numbers, and basic punctuation';
    }

    if (!formData.country) {
      newErrors.country = 'Country is required';
    }

    if (!validatePhone(formData.mobileNumber)) {
      newErrors.mobileNumber = 'Valid mobile number is required';
    }

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Valid email address is required';
    }

    if (!validateMessage(formData.message)) {
      newErrors.message = 'Message is required and can only contain letters, numbers, and basic punctuation';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Store signup data (in real app, this would be sent to backend)
      localStorage.setItem('signupData', JSON.stringify(formData));
      alert('Thank you for signing up! We will contact you soon.');
      navigate('/login');
    }
  };

  return (
    <div className="signup-page">
      <header className="signup-header">
        <Link to="/" className="logo-link">
          <div className="logo-container">
            <img src={logoUrl} alt="Connect Lane" className="logo-img" />
            <span className="logo-text"><span className="logo-connect">Connect</span> <span className="logo-lane">Lane</span></span>
          </div>
        </Link>
        <Link to="/" className="back-link">
          <span className="back-icon">?</span>
          <span>Back to home</span>
        </Link>
      </header>

      <div className="signup-container">
        <div className="signup-left">
          <div className="secure-badge">
            <span className="badge-icon">🛡️</span>
            <span>SECURE & REGULATED</span>
          </div>
          
          <h1 className="welcome-title">Join Connect Lane for Global Transfers</h1>
          
          <p className="welcome-description">
            Get started with our secure remittance platform. Fill out the form and our team will get in touch with you shortly.
          </p>

          <div className="features-list">
            <div className="feature-item">
              <div className="feature-icon">🔌</div>
              <div className="feature-content">
                <h3>Multiple Partners</h3>
                <p>Connect with Zenvoya Remit, PaySutra Global, RemitRaah, TransFeriya and more.</p>
              </div>
            </div>
            
            <div className="feature-item">
              <div className="feature-icon">🔑</div>
              <div className="feature-content">
                <h3>Unified API</h3>
                <p>Single standardized API interface for all integrations.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="signup-right">
          <h2 className="form-title">Sign Up</h2>
          <p className="form-subtitle">Fill in your contact information to get started.</p>

          <form onSubmit={handleSubmit} className="signup-form">
            <div className="form-group">
              <label>NAME *</label>
              <input
                type="text"
                name="name"
                className={`form-input ${errors.name ? 'error' : ''}`}
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your full name"
                required
              />
              {errors.name && <span className="error-message">{errors.name}</span>}
            </div>

            <div className="form-group">
              <label>ORGANIZATION NAME *</label>
              <input
                type="text"
                name="organizationName"
                className={`form-input ${errors.organizationName ? 'error' : ''}`}
                value={formData.organizationName}
                onChange={handleChange}
                placeholder="Enter your organization name"
                required
              />
              {errors.organizationName && <span className="error-message">{errors.organizationName}</span>}
            </div>

            <div className="form-group">
              <label>COUNTRY *</label>
              <select
                name="country"
                className={`form-input ${errors.country ? 'error' : ''}`}
                value={formData.country}
                onChange={handleChange}
                required
              >
                <option value="">Select your country</option>
                {countries.map(country => (
                  <option key={country} value={country}>{country}</option>
                ))}
              </select>
              {errors.country && <span className="error-message">{errors.country}</span>}
            </div>

            <div className="form-group">
              <label>MOBILE NUMBER *</label>
              <input
                type="tel"
                name="mobileNumber"
                className={`form-input ${errors.mobileNumber ? 'error' : ''}`}
                value={formData.mobileNumber}
                onChange={handleChange}
                placeholder="+1-234-567-8900"
                required
              />
              {errors.mobileNumber && <span className="error-message">{errors.mobileNumber}</span>}
            </div>

            <div className="form-group">
              <label>EMAIL ADDRESS *</label>
              <input
                type="email"
                name="email"
                className={`form-input ${errors.email ? 'error' : ''}`}
                value={formData.email}
                onChange={handleChange}
                placeholder="name@company.com"
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label>MESSAGE *</label>
              <textarea
                name="message"
                className={`form-input form-textarea ${errors.message ? 'error' : ''}`}
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us about your requirements"
                rows="4"
                required
              />
              {errors.message && <span className="error-message">{errors.message}</span>}
            </div>

            <button type="submit" className="submit-btn">
              Submit
            </button>
          </form>

          <p className="login-link">
            Already have an account? <Link to="/login">Log In</Link>
          </p>

          <p className="terms-text">
            By continuing, you agree to Connect Lane's <Link to="#">Terms of Service</Link> and <Link to="#">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignupPage;
